import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/Card/Card';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import Loading from '../../components/Loading/Loading';
import api from '../../services/api';
import { getErrorMessage } from '../../utils/helpers';
import './GuestForm.scss';

const GuestForm: React.FC = () => {
  const { eventId, inviteId, guestId } = useParams<{ eventId: string; inviteId: string; guestId?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEdit = Boolean(guestId);

  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState('');

  const fetchGuest = useCallback(async () => {
    if (!user?.uuid || !eventId || !inviteId || !guestId) return;

    try {
      setFetchLoading(true);
      const guest = await api.getGuest(user.uuid, eventId, inviteId, guestId);
      setName(guest.name);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setFetchLoading(false);
    }
  }, [user, eventId, inviteId, guestId]);

  useEffect(() => {
    if (isEdit && guestId) {
      fetchGuest();
    }
  }, [isEdit, guestId, fetchGuest]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uuid || !eventId || !inviteId) return;

    setError('');
    setLoading(true);

    try {
      const guestData = {
        name: name.trim(),
      };

      if (isEdit && guestId) {
        await api.updateGuest(user.uuid, eventId, inviteId, guestId, guestData);
      } else {
        await api.createGuest(user.uuid, eventId, inviteId, guestData);
      }
      navigate(`/events/${eventId}`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <Loading />;
  }

  return (
    <div className="guest-form-page">
      <div className="container">
        <Card className="guest-form-card">
          <h1>{isEdit ? 'Edit Guest' : 'Add Guest'}</h1>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <Input
              label="Guest Name"
              type="text"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Enter guest name"
            />

            <div className="form-actions">
              <Button
                variant="outline"
                onClick={() => navigate(`/events/${eventId}`)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update Guest' : 'Add Guest')}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default GuestForm;
